'use strict';

import { Entity } from '../Entity';
import type { Ind } from '../Ind/Ind';
import type { User } from './User';
import type { UserCompanyLogo } from '../User/UserCompanyLogo';
import type { UserSignature } from './UserSignature';
import type { UserSignatureEmail } from './UserSignatureEmail';
import type { UserSignaturePic } from './UserSignaturePic';
import type { UserClientPortal } from '../User/UserClientPortal';
/**
 * Types
 */
export const USER_LETTER_TYPE_TYPE = {
  EMAIL: 'Email',
  LETTER: 'Letter',
  SMS: 'SMS',
  ALL: 'All',
} as const;

export type USER_LETTER_TYPE_TYPE =
  (typeof USER_LETTER_TYPE_TYPE)[keyof typeof USER_LETTER_TYPE_TYPE];

export class UserLetter extends Entity {
  protected letterId: number;
  protected userId;
  protected letterType;
  protected letterTitle;
  protected letterBody;
  protected letterDate;
  protected letterLang;
  protected letterParent;
  protected attachments;
  protected sharing;
  protected folder;
  protected letterSubject;
  constructor() {
    super();
  }

  public hydrate(data: any) {
    this.letterId = data.letterId;
    this.letterType = data.letterType;
    this.letterTitle = data.letterTitle;
    this.letterBody = data.letterBody;
    this.letterLang = data.letterLang;
    this.letterParent = data.letterParent;
    this.attachments = data.attachments;
    this.sharing = data.sharing;
    this.folder = data.folder;
    this.letterSubject = data.letterSubject;

    if (data.letterDate) {
      const letterDate = data.letterDate.date.split(' ')[0].split('-');
      this.letterDate = letterDate[1] + '/' + letterDate[2] + '/' + letterDate[0];
    }
  }

  public getLetterId() {
    return this.letterId;
  }

  public getLetterType() {
    return this.letterType;
  }

  public setLetterType(letterType) {
    this.letterType = letterType;
    return this;
  }

  public getLetterTitle() {
    return this.letterTitle;
  }

  public setLetterTitle(letterTitle) {
    this.letterTitle = letterTitle;
    return this;
  }

  public getLetterBody() {
    return this.letterBody;
  }

  public setLetterBody(letterBody) {
    this.letterBody = letterBody;
    return this;
  }

  public getFolder() {
    return this.folder;
  }

  public setFolder(folder) {
    this.folder = folder;
    return this;
  }

  public getLetterSubject() {
    return this.letterSubject;
  }

  public setLetterSubject(letterSubject) {
    this.letterSubject = letterSubject;
    return this;
  }

  public renderLetterBody(
    individual: Ind,
    user: User,
    signature: UserSignature = null,
    signatureEmail: UserSignatureEmail = null,
    signaturePic: UserSignaturePic = null,
    companyLogo: UserCompanyLogo = null,
    mergeSpouse = true,
    templateType = null,
    processType = null,
    userClientPortal: UserClientPortal
  ) {
    let letterBody = this.getLetterBody();
    const date = new Date();
    const dateString =
      date.getMonth() +
      1 +
      '/' +
      (date.getDate() < 10 ? '0' + date.getDate() : date.getDate()) +
      '/' +
      date.getFullYear();

    let address = individual.getMailingAddress();
    let fullAddress = '';

    const spouse = individual.getSpouse();

    if (!address && spouse) {
      address = spouse.getMailingAddress();
    }

    if (!address) {
      address = individual.getDefaultAddress();
    }

    if (!address && spouse) {
      address = spouse.getDefaultAddress();
    }

    if (!address) {
      address = individual.getAddresses()[0];
    }

    if (!address && spouse) {
      address = spouse.getAddresses()[0];
    }

    if (address) {
      fullAddress +=
        address.getIndAddressStreet() +
        (address.getIndAddressUnit() ? ' ' + address.getIndAddressUnit() : '');
      fullAddress += '<br />';
      fullAddress +=
        address.getIndAddressCity() +
        ', ' +
        address.getIndAddressState() +
        ' ' +
        address.getIndAddressZip();
    }

    const policies = individual.getPolicies();

    let fullPolicies = '';
    let newestPolicy = null;
    let newestPolicyHtml = '';
    let policyBenefitReminderHTML = '';

    const providers = individual.getProviders();
    let pcp = null;

    if (policies) {
      policies.getItems().forEach((policy) => {
        if (['Pending', 'Active', 'Active-NoComp'].includes(policy.getPolicyStatus())) {
          if (!newestPolicy) {
            newestPolicy = policy;
          } else {
            const newestPolicyEffective = new Date(newestPolicy.getPolicyEffective());
            const thisPolicyEffective = new Date(policy.getPolicyEffective());
            if (thisPolicyEffective > newestPolicyEffective) {
              newestPolicy = policy;
            }
          }

          fullPolicies +=
            '<div class="card border border-primary bg-white mb-3" style="height:auto;margin-bottom:20px;">';

          if (policy.getCompany()) {
            fullPolicies +=
              '<div class="card-header"><b>' +
              policy.getCompany().getCompanyName() +
              '</b></div>' +
              '\r';
          }

          fullPolicies += '<div class="card-body pt-2">';
          fullPolicies += '<div><i>Insured:</i> ' + individual.getIndName() + '</div>' + '\r';

          if (policy.getCompany()) {
            fullPolicies +=
              '<div><i>Policy Company:</i> ' +
              policy.getCompany().getCompanyName() +
              '</div>' +
              '\r';
          }
          if (policy.getPlan()) {
            fullPolicies +=
              '<div><i>Policy Name:</i> ' + policy.getPlan().getPlanName() + '</div>' + '\r';
          }
          fullPolicies +=
            '<div><i>Policy Status:</i> ' + policy.getPolicyStatus() + '</div>' + '\r';
          fullPolicies += '<div><i>Policy Type:</i> ' + policy.getPolicyType() + '</div>' + '\r';
          fullPolicies +=
            '<div><i>Effective Date:</i> ' + policy.getPolicyEffective() + '</div>' + '\r';

          if (policy.getPolicyNumber()) {
            fullPolicies +=
              '<div><i>Policy Number:</i> ' + policy.getPolicyNumber() + '</div>' + '\r';
          }

          if (policy.getPolicyPremium()) {
            const noPrem = ['MAPD', 'PDP'];
            if (!noPrem.includes(policy.getPolicyType())) {
              if (policy.getPolicyPremium() != '0.00') {
                fullPolicies +=
                  '<div><i>Premium:</i> $' + policy.getPolicyPremium() + '</div>' + '\r';
              }
            }
          }

          fullPolicies += '</div></div>';
        }
      });

      if (newestPolicy) {
        newestPolicyHtml +=
          '<div class="card border border-primary bg-white mb-3" style="height:auto">' +
          '<div class="card-header">' +
          newestPolicy.getCompany().getCompanyName() +
          '</div>' +
          '<div class="card-body">' +
          '<div><i>Policy Status:</i> ' +
          newestPolicy.getPolicyStatus() +
          '</div>' +
          '<div><i>Policy Name:</i> ' +
          newestPolicy.getPlan().getPlanName() +
          '</div>' +
          '<div><i>Effective Date:</i> ' +
          newestPolicy.getPolicyEffective() +
          '</div>';
        if (newestPolicy.getPolicyNumber() != '') {
          newestPolicyHtml +=
            '<div><i>Policy Number:</i> ' + newestPolicy.getPolicyNumber() + '</div>';
        }

        if (newestPolicy.getPolicyPremium() > 0 && newestPolicy.getPolicyPremium() != '') {
          newestPolicyHtml += '<div><i>Premium:</i> $' + newestPolicy.getPolicyPremium() + '</div>';
        }

        newestPolicyHtml += '</div></div>';
      }
    }

    if (providers) {
      providers.getItems().forEach((provider) => {
        if (provider.getIndProviderSpecialty() == 'PCP') {
          pcp = provider;
        }
      });
    }

    // Lets see if they are using a custom portal
    let portalId = '';
    let portalPlaceholder = null;
    let portalQRPlaceholder = null;
    if (letterBody.indexOf('{{CP-') >= 0) {
      // need to get the ID of the portal they are using
      var index = letterBody.indexOf('{{CP-');
      var rawString = letterBody.substring(index);
      var parts = rawString.split('-');
      portalId = parts[2].split('}}')[0];
      portalPlaceholder = parts[0] + '-' + parts[1] + '-' + portalId + '}}';
    }
    // Custom Portal QR
    if (letterBody.indexOf('{{CPQR-') >= 0) {
      // need to get the ID of the portal they are using
      var index = letterBody.indexOf('{{CPQR-');
      var rawString = letterBody.substring(index);
      var parts = rawString.split('-');
      portalId = parts[2].split('}}')[0];
      portalQRPlaceholder = parts[0] + '-' + parts[1] + '-' + portalId + '}}';
    }

    const protocol = window.location.hostname.indexOf('localhost') >= 0 ? 'http://' : 'https://';
    const sigImgUrl = protocol + window.location.hostname + '/img/users/';
    const logoUrl = protocol + window.location.hostname + '/img/users/';

    let portalurl = protocol + window.location.hostname + '/agent/client/auth/?';

    if (portalId) {
      portalurl = portalurl + 'pid=' + portalId + '&acui=';
    } else {
      portalurl = portalurl + 'acui=';
    }

    const qrcodeurl = protocol + window.location.hostname + '/api/v1/qr-codes.png?data=';

    const acui =
      processType == 'single' ? individual.getIndIdCrypto() : individual.getIndIdCrypto(); //'{{PRTLACCESS}}'

    var fullPortal =
      '<p>Your password is your last name followed by your date of birth (mmddyyyy), all lowercase.</p><p><b> Example: smith09191925</b></p><p style="font-weight:900;font-size:12pt;">' +
      (templateType == 'sms'
        ? '\r' + portalurl + individual.getIndIdCrypto() + '\r'
        : '<a href="' + portalurl + acui + '">Your Client Portal</a>') +
      '</p>';

    var qrCodePortal =
      '<p>Your password is your last name followed by your date of birth (mmddyyyy), all lowercase.</p><p><b> Example: smith09191925</b></p><p style="font-weight:900;font-size:12pt;"><img src="' +
      qrcodeurl +
      btoa(portalurl + acui) +
      '" border="0" /></p>';

    if (individual.getPrefLang()) {
      if (individual.getPrefLang()?.getIndPrefLang() === 'ESP') {
        var fullPortal =
          '<p>Su contraseña es su apellido seguido de su fecha de nacimiento (mmddyyyy), todo en minúsculas.</p><p><b> Ejemplo: lopez09191925</b></p><p style="font-weight:900;font-size:12pt;">' +
          (templateType == 'sms'
            ? '\r' + portalurl + individual.getIndIdCrypto() + '\r'
            : '<a href="' + portalurl + acui + '">Su portal de clientes</a>') +
          '</p>';

        var qrCodePortal =
          '<p>Su contraseña es su apellido seguido de su fecha de nacimiento (mmddyyyy), todo en minúsculas.</p><p><b> Ejemplo: lopez09191925</b></p><p style="font-weight:900;font-size:12pt;"><img src="' +
          qrcodeurl +
          btoa(portalurl + acui) +
          '" border="0" /></p>';
      }
    }

    const replacementList = [
      {
        field: 'PROFILE-ID',
        bracesField: '{{PROFILE-ID}}',
        value: individual.getIndId(),
      },
      {
        field: 'DATE',
        bracesField: '{{DATE}}',
        value: dateString,
      },
      {
        field: 'LASTNAME',
        bracesField: '{{LASTNAME}}',
        value: individual.getIndLastname(),
      },
      {
        field: 'PREFIX',
        bracesField: '{{PREFIX}}',
        value: individual.renderIndPrefix() || '',
      },
      {
        field: 'ADDRESS',
        bracesField: '{{ADDRESS}}',
        value: fullAddress,
      },
      {
        field: 'POLICY',
        bracesField: '{{POLICY}}',
        value: fullPolicies,
      },
      {
        field: 'SIGNATURE',
        bracesField: '{{SIGNATURE}}',
        value: signature ? signature.getSignature() : '',
      },
      {
        field: 'SIG-EMAIL',
        bracesField: '{{SIG-EMAIL}}',
        value: signatureEmail ? signatureEmail.getSignature() : '',
      },
      {
        field: 'SIG-IMG',
        bracesField: '{{SIG-IMG}}',
        value: signaturePic
          ? '<img src="' +
            sigImgUrl +
            signaturePic.getSignaturePicName() +
            '" alt="" style="max-width:600px;max-height:150px;"  class=" img mt-2" >'
          : '',
      },
      {
        field: 'LOGO',
        bracesField: '{{LOGO}}',
        value: companyLogo
          ? '<img src="' +
            logoUrl +
            companyLogo.getCompanyLogoName() +
            '" alt=""  class=" img mt-2" style="max-width:255px">'
          : '',
      },
      {
        field: 'PORTAL',
        bracesField: '{{PORTAL}}',
        value: fullPortal,
      },
      {
        field: 'QRCODE',
        bracesField: '{{QRCODE}}',
        value: qrCodePortal,
      },
      {
        field: 'POLNEW',
        bracesField: '{{POLNEW}}',
        value: newestPolicyHtml,
      },
      {
        field: 'MY-NAME-FIRST',
        bracesField: '{{MY-NAME-FIRST}}',
        value: user.getUserFirstname(),
      },
      {
        field: 'MY-NAME-LAST',
        bracesField: '{{MY-NAME-LAST}}',
        value: user.getUserLastname(),
      },
      {
        field: 'MY-COMPANY-NAME',
        bracesField: '{{MY-COMPANY-NAME}}',
        value: user.getUserCompanyName(),
      },
      {
        field: 'MY-EMAIL',
        bracesField: '{{MY-EMAIL}}',
        value: user.getUserUsername(),
      },
      {
        field: 'MY-PHONE',
        bracesField: '{{MY-PHONE}}',
        value: user.getUserPhone(),
      },
      {
        field: 'INDIVIDUAL-PHONE',
        bracesField: '{{INDIVIDUAL-PHONE}}',
        value: individual.getAnyPhone() ? individual.getAnyPhone().getIndPhoneNumber() : '',
      },
      {
        field: 'INDIVIDUAL-EMAIL',
        bracesField: '{{INDIVIDUAL-EMAIL}}',
        value: individual.getAnyEmail() ? individual.getAnyEmail().getIndEmailAddress() : '',
      },
    ];

    // Find variable portal placeholders
    if (portalQRPlaceholder) {
      replacementList.push({
        field: portalQRPlaceholder,
        bracesField: portalQRPlaceholder,
        value: qrCodePortal,
      });
    }

    if (portalPlaceholder) {
      replacementList.push({
        field: portalPlaceholder,
        bracesField: portalPlaceholder,
        value: fullPortal,
      });
    }

    if (!spouse || (spouse && spouse.getIndId() === individual.getIndId()) || !mergeSpouse) {
      const fullname = {
        field: 'FULLNAME',
        bracesField: '{{FULLNAME}}',
        value: individual.getIndName().trim(),
      };

      const firstname = {
        field: 'FIRSTNAME',
        bracesField: '{{FIRSTNAME}}',
        value: individual.getIndNickname() || individual.getIndFirstname(),
      };

      const firstnameStrict = {
        field: 'FIRST-STRICT',
        bracesField: '{{FIRST-STRICT}}',
        value: individual.getIndFirstname(),
      };

      replacementList.push(fullname);
      replacementList.push(firstnameStrict);
      replacementList.push(firstname);
    }

    // If ind has a spouse
    if (spouse && spouse.getIndId() !== individual.getIndId()) {
      // If spouse should be on label
      if (mergeSpouse) {
        const fullnameMerge = {
          field: 'FULLNAME',
          bracesField: '{{FULLNAME}}',
          value: individual.getIndJointSpouseName(),
        };

        const firstnameMerge = {
          field: 'FIRSTNAME',
          bracesField: '{{FIRSTNAME}}',
          value: individual.getIndJointSpouseFirstName(1),
        };

        const firstnameStrictMerge = {
          field: 'FIRST-STRICT',
          bracesField: '{{FIRST-STRICT}}',
          value: individual.getIndJointSpouseFirstName(),
        };

        replacementList.push(firstnameStrictMerge);
        replacementList.push(firstnameMerge);
        replacementList.push(fullnameMerge);
      }
    }

    const newPolicyCompany = {
      field: 'NEW-POL-COMPANY',
      bracesField: '{{NEW-POL-COMPANY}}',
      value: newestPolicy ? newestPolicy.getCompany().getCompanyName() : '',
    };

    const newPolicyPlan = {
      field: 'NEW-POL-PLAN',
      bracesField: '{{NEW-POL-PLAN}}',
      value: newestPolicy ? newestPolicy.getPlan().getPlanName() : '',
    };

    const newPolicyEffective = {
      field: 'NEW-POL-EFFECTIVE',
      bracesField: '{{NEW-POL-EFFECTIVE}}',
      value: newestPolicy ? newestPolicy.getPolicyEffective() : '',
    };

    const newPolicyPremium = {
      field: 'NEW-POL-PREMIUM',
      bracesField: '{{NEW-POL-PREMIUM}}',
      value: newestPolicy ? '$' + newestPolicy.getPolicyPremium() : '',
    };

    const newPolicySubsidy = {
      field: 'NEW-POL-SUBSIDY',
      bracesField: '{{NEW-POL-SUBSIDY}}',
      value: newestPolicy ? '$' + newestPolicy.getPolicySubsidy() : '',
    };

    const newPolicyNumber = {
      field: 'NEW-POL-NUMBER',
      bracesField: '{{NEW-POL-NUMBER}}',
      value: newestPolicy ? newestPolicy.getPolicyNumber() : '',
    };

    if (newestPolicy) {
      if (newestPolicy.getPolicyBenefitReminder()) {
        policyBenefitReminderHTML = '<ol>';
        newestPolicy.getPolicyBenefitReminder().forEach((item) => {
          policyBenefitReminderHTML += '<li>' + item + '</li>';
        });
        policyBenefitReminderHTML += '</ol>';
      }
    }

    const newPolicyBenefitsReminder = {
      field: 'NEW-POL-BENEFITS',
      bracesField: '{{NEW-POL-BENEFITS}}',
      value: policyBenefitReminderHTML,
    };

    replacementList.push(newPolicyCompany);
    replacementList.push(newPolicyPlan);
    replacementList.push(newPolicyEffective);
    replacementList.push(newPolicyPremium);
    replacementList.push(newPolicyBenefitsReminder);
    replacementList.push(newPolicyNumber);
    replacementList.push(newPolicySubsidy);

    const pcpName = {
      field: 'PCP-NAME',
      bracesField: '{{PCP-NAME}}',
      value: pcp ? pcp.getIndProviderFirstname() + ' ' + pcp.getIndProviderLastname() : '',
    };

    const pcpFacility = {
      field: 'PCP-FACILITY',
      bracesField: '{{PCP-FACILITY}}',
      value: pcp ? pcp.getIndProviderFacility() : '',
    };

    const pcpPhone = {
      field: 'PCP-PHONE',
      bracesField: '{{PCP-PHONE}}',
      value: pcp ? pcp.getIndProviderPhone() : '',
    };

    const pcpAddress = {
      field: 'PCP-ADDR',
      bracesField: '{{PCP-ADDR}}',
      value: pcp ? pcp.getIndProviderAddress() : '',
    };

    replacementList.push(pcpName);
    replacementList.push(pcpFacility);
    replacementList.push(pcpPhone);
    replacementList.push(pcpAddress);

    const upsertStatus = {
      field: 'UPSERT-POL-STATUS',
      bracesField: '{{UPSERT-POL-STATUS}}',
      value: '',
    };

    const upsertCompany = {
      field: 'UPSERT-POL-COMPANY',
      bracesField: '{{UPSERT-POL-COMPANY}}',
      value: '',
    };

    const upsertPlan = {
      field: 'UPSERT-POL-PLAN',
      bracesField: '{{UPSERT-POL-PLAN}}',
      value: '',
    };

    const upsertPremium = {
      field: 'UPSERT-POL-PREMIUM',
      bracesField: '{{UPSERT-POL-PREMIUM}}',
      value: '',
    };

    const upsertSubsidy = {
      field: 'UPSERT-POL-SUBSIDY',
      bracesField: '{{UPSERT-POL-SUBSIDY}}',
      value: '',
    };

    const upsertEffective = {
      field: 'UPSERT-POL-EFFECTIVE',
      bracesField: '{{UPSERT-POL-EFFECTIVE}}',
      value: '',
    };

    const upsertAorFirst = {
      field: 'UPSERT-POL-AOR-FRST',
      bracesField: '{{UPSERT-POL-AOR-FRST}}',
      value: '',
    };

    const upsertAorLast = {
      field: 'UPSERT-POL-AOR-LST',
      bracesField: '{{UPSERT-POL-AOR-LST}}',
      value: '',
    };

    const upsertAorEmail = {
      field: 'UPSERT-POL-AOR-EML',
      bracesField: '{{UPSERT-POL-AOR-EML}}',
      value: '',
    };

    const upsertAorPhone = {
      field: 'UPSERT-POL-AOR-PHN',
      bracesField: '{{UPSERT-POL-AOR-PHN}}',
      value: '',
    };

    replacementList.push(upsertStatus);
    replacementList.push(upsertCompany);
    replacementList.push(upsertEffective);
    replacementList.push(upsertPlan);
    replacementList.push(upsertPremium);
    replacementList.push(upsertSubsidy);
    replacementList.push(upsertAorFirst);
    replacementList.push(upsertAorLast);
    replacementList.push(upsertAorEmail);
    replacementList.push(upsertAorPhone);

    const toBase64Url = (str) => {
      const bytes = new TextEncoder().encode(str);
      let bin = '';
      for (const b of bytes) bin += String.fromCharCode(b);
      const b64 = btoa(bin);
      return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    };

    let unsub = '';
    if (individual.getAnyEmail()) {
      const raw =
        `${individual.getIndId()}|${individual.getAnyEmail().getIndEmailAddress()}`.trim();
      unsub = toBase64Url(raw);
    }
    const unsubscribeObj = {
      field: 'UNSUBSCRIBE',
      bracesField: '{{UNSUBSCRIBE}}',
      value:
        '<div style="margin-top:20px;font-size:10pt;">If you\'d prefer not to receive future emails from us, you can unsubscribe at any time by clicking <a href="https://www.medicareproapp.com/agent/client/unsubscribe/' +
        unsub +
        '" target="_blank">here.</a></div>',
    };
    replacementList.push(unsubscribeObj);
    // policy type specific
    const policyTypeSpecificMergeTabsSearchTerms = [
      {
        label: 'NEW-MAPD-',
        value: 'MAPD',
        list: [
          'COMPANY',
          'PLAN',
          'EFFECTIVE',
          'NUMBER',
          'BENEFITS',
          'AOR-FRST',
          'AOR-LST',
          'AOR-EML',
          'AOR-PHN',
        ],
      },
      {
        label: 'NEW-PDP-',
        value: 'PDP',
        list: [
          'COMPANY',
          'PLAN',
          'EFFECTIVE',
          'NUMBER',
          'BENEFITS',
          'AOR-FRST',
          'AOR-LST',
          'AOR-EML',
          'AOR-PHN',
        ],
      },
      {
        label: 'NEW-SUP-',
        value: 'SUP',
        list: [
          'COMPANY',
          'PLAN',
          'EFFECTIVE',
          'NUMBER',
          'PREMIUM',
          'PREM-DT',
          'AOR-FRST',
          'AOR-LST',
          'AOR-EML',
          'AOR-PHN',
        ],
      },
      {
        label: 'NEW-IND-',
        value: 'IND',
        list: [
          'COMPANY',
          'PLAN',
          'EFFECTIVE',
          'NUMBER',
          'PREMIUM',
          'PREM-TOT',
          'SUBSIDY',
          'AOR-FRST',
          'AOR-LST',
          'AOR-EML',
          'AOR-PHN',
        ],
      },
      {
        label: 'NEW-ANC-',
        value: 'ANC',
        list: [
          'COMPANY',
          'PLAN',
          'EFFECTIVE',
          'NUMBER',
          'PREMIUM',
          'AOR-FRST',
          'AOR-LST',
          'AOR-EML',
          'AOR-PHN',
        ],
      },
      {
        label: 'NEW-LIFE-',
        value: 'LIFE',
        list: [
          'COMPANY',
          'PLAN',
          'EFFECTIVE',
          'NUMBER',
          'PREMIUM',
          'DB',
          'AOR-FRST',
          'AOR-LST',
          'AOR-EML',
          'AOR-PHN',
        ],
      },
      {
        label: 'NEW-ANNUITY-',
        value: 'ANNUITY',
        list: [
          'COMPANY',
          'PLAN',
          'EFFECTIVE',
          'NUMBER',
          'PREMIUM',
          'AOR-FRST',
          'AOR-LST',
          'AOR-EML',
          'AOR-PHN',
        ],
      },
    ];

    policyTypeSpecificMergeTabsSearchTerms.forEach((searchTerm) => {
      if (letterBody.indexOf(searchTerm.label) >= 0) {
        const newestPolicyByType = individual.getMostRecentPolicyByType(searchTerm.value);
        //console.log(searchTerm.value,newestPolicyByType);
        searchTerm.list.forEach((mergeItem) => {
          let value = null;

          if (mergeItem === 'COMPANY' && newestPolicyByType) {
            value = newestPolicyByType.getCompany().getCompanyName();
          }
          if (mergeItem === 'PLAN' && newestPolicyByType) {
            value = newestPolicyByType.getPlan().getPlanName();
          }
          if (mergeItem === 'EFFECTIVE' && newestPolicyByType) {
            value = newestPolicyByType.getPolicyEffective();
          }
          if (mergeItem === 'NUMBER' && newestPolicyByType) {
            value = newestPolicyByType.getPolicyNumber();
          }
          if (mergeItem === 'PREMIUM' && newestPolicyByType) {
            value =
              newestPolicyByType.getPolicyPremium() > 0
                ? '$' + newestPolicyByType.getPolicyPremium()
                : '';
          }
          if (mergeItem === 'PREM-TOT' && newestPolicyByType) {
            value =
              newestPolicyByType.getPolicyPremiumTotal() > 0
                ? '$' + newestPolicyByType.getPolicyPremiumTotal()
                : '';
          }
          if (mergeItem === 'PREM-DT' && newestPolicyByType) {
            console.log(newestPolicyByType);
            if (newestPolicyByType.getPolicyAccountValues()) {
              const accVals = newestPolicyByType.getPolicyAccountValues().getItems();
              value = accVals[accVals.length - 1].getAnniversaryDate();
            }
          }
          if (mergeItem === 'SUBSIDY' && newestPolicyByType) {
            value =
              newestPolicyByType.getPolicySubsidy() > 0
                ? '$' + newestPolicyByType.getPolicySubsidy()
                : '';
          }
          if (mergeItem === 'DB' && newestPolicyByType) {
            value = newestPolicyByType.getPolicyDb() ? '$' + newestPolicyByType.getPolicyDb() : '';
          }
          if (mergeItem === 'BENEFITS' && newestPolicyByType) {
            console.log('benefits');
            console.log(newestPolicyByType.getPolicyBenefitReminder());
            if (newestPolicyByType.getPolicyBenefitReminder()) {
              console.log(newestPolicyByType.getPolicyBenefitReminder());
              value = '<ol>';
              newestPolicyByType.getPolicyBenefitReminder().forEach((item) => {
                value += '<li>' + item + '</li>';
              });
              value += '</ol>';
            }
          }
          if (mergeItem === 'AOR-FRST' && newestPolicyByType && newestPolicyByType.getPolicyAor()) {
            value = newestPolicyByType.getPolicyAor().getUserFirstname();
          }
          if (mergeItem === 'AOR-LST' && newestPolicyByType && newestPolicyByType.getPolicyAor()) {
            value = newestPolicyByType.getPolicyAor().getUserLastname();
          }
          if (mergeItem === 'AOR-EML' && newestPolicyByType && newestPolicyByType.getPolicyAor()) {
            value = newestPolicyByType.getPolicyAor().getUserUsername();
          }
          if (mergeItem === 'AOR-PHN' && newestPolicyByType && newestPolicyByType.getPolicyAor()) {
            value = newestPolicyByType.getPolicyAor().getUserPhone();
          }

          replacementList.push({
            field: searchTerm.label + mergeItem,
            bracesField: '{{' + searchTerm.label + mergeItem + '}}',
            value: value ? value : '',
          });
        });
      }
    });

    replacementList.forEach((replacement) => {
      letterBody = letterBody.replaceAll(replacement.bracesField, replacement.value);
      if (replacement.field === 'POLNEW') {
        letterBody = letterBody.replaceAll(replacement.field, replacement.value);
      }
      //letterBody = letterBody.replaceAll(replacement.field, replacement.value)
    });

    return letterBody;
  }

  public getLetterDate() {
    return this.letterDate;
  }

  public getLetterLang() {
    return this.letterLang;
  }

  public getLetterParent() {
    return this.letterParent;
  }

  public getAttachments() {
    return this.attachments;
  }

  public setAttachments(attachments) {
    this.attachments = attachments;
    return this;
  }

  public getSharing() {
    return this.sharing;
  }
}
